import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getTotalByApp, getTotalByAppByMonth } from '../../services/ticketService';
import { getActiveAppOriginCodes, getAppOriginFilterOptions } from '../../services/appOriginService';
import FieldFilter from '../../components/shared/FieldFilter';
import CollapsibleFilters from '../../components/shared/CollapsibleFilters';
import { getStatusFilterOptions } from '../../services/statusService';
import { useApp } from '../../context/AppContext';
import { PRIORITY_FILTER_OPTIONS } from '../../services/priorityService';
import TotalTicketsByAppChart from '../../components/dashboards/TotalTicketsByAppChart';
import TotalTicketsByAppStatusChart, {
  APP_STATUS_KEYS,
  buildAppStatusRows,
  parseTotalByAppList,
  resolveDemandTypeSeriesKeys,
} from '../../components/dashboards/TotalTicketsByAppStatusChart';
import DashboardDemandTypeFilter from '../../components/dashboards/DashboardDemandTypeFilter';
import { getByAppMonthRangeLocalDates, BY_APP_MONTH_WINDOW, TIME_ZONE } from '../../utils/dateUtils';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const ChartsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

const ChartBlock = styled.div`
  width: 100%;
  min-width: 0;
`;

const FiltersPanelBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: flex-end;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TotalTicketsByAppPage = () => {
  const { ticketStatuses, appOrigins } = useApp();
  const statusFilterOptions = useMemo(() => getStatusFilterOptions(), [ticketStatuses]);
  // ===== Gráfico por STATUS =====
  const [totalByAppStatus, setTotalByAppStatus] = useState([]);
  const [totalByAppStatusLoading, setTotalByAppStatusLoading] = useState(true);
  const [totalByAppStatusError, setTotalByAppStatusError] = useState(false);

  // ===== Gráfico por MÊS =====
  const [totalByAppMonthContent, setTotalByAppMonthContent] = useState([]);
  const [totalByAppMonthLoading, setTotalByAppMonthLoading] = useState(true);
  const [totalByAppMonthError, setTotalByAppMonthError] = useState(false);
  const [byAppFilterStatus, setByAppFilterStatus] = useState([]);
  const [byAppFilterPriority, setByAppFilterPriority] = useState([]);
  const [byAppFilterAppOrigin, setByAppFilterAppOrigin] = useState([]);
  const [byAppFilterDemandType, setByAppFilterDemandType] = useState([]);

  const allDashboardAppOrigins = useMemo(() => [...getActiveAppOriginCodes()], [appOrigins]);

  const appOriginFilterItems = useMemo(() => getAppOriginFilterOptions(), [appOrigins]);

  /** Ordem das séries: com filtro de app, só as origens selecionadas (na ordem do dashboard). */
  const byAppChartSeriesOrder = useMemo(() => {
    if (!Array.isArray(byAppFilterAppOrigin) || byAppFilterAppOrigin.length === 0) {
      return allDashboardAppOrigins;
    }
    const selected = new Set(byAppFilterAppOrigin.map((o) => String(o)));
    return allDashboardAppOrigins.filter((o) => selected.has(String(o)));
  }, [byAppFilterAppOrigin, allDashboardAppOrigins]);

  const fetchTotalByAppStatus = useCallback(async () => {
    setTotalByAppStatusLoading(true);
    setTotalByAppStatusError(false);
    try {
      const origins = Array.isArray(allDashboardAppOrigins) ? allDashboardAppOrigins : [];
      if (origins.length === 0) {
        setTotalByAppStatus([]);
        return;
      }

      const seriesKeys = resolveDemandTypeSeriesKeys(byAppFilterDemandType);
      const jobs = APP_STATUS_KEYS.flatMap((status) =>
        seriesKeys.map((type) => ({
          status,
          type,
          request: getTotalByApp({
            status: [status],
            demandType: [type],
          }),
        }))
      );
      const responses = await Promise.all(jobs.map((job) => job.request));
      const byStatusAndType = {};
      APP_STATUS_KEYS.forEach((status) => {
        byStatusAndType[status] = {};
      });
      jobs.forEach((job, index) => {
        byStatusAndType[job.status][job.type] = parseTotalByAppList(responses[index]);
      });
      setTotalByAppStatus(buildAppStatusRows(origins, byStatusAndType, seriesKeys));
    } catch (err) {
      console.error('Error fetching total by app (status):', err);
      setTotalByAppStatus([]);
      setTotalByAppStatusError(true);
    } finally {
      setTotalByAppStatusLoading(false);
    }
  }, [allDashboardAppOrigins, byAppFilterDemandType]);

  const fetchTotalByAppByMonth = useCallback(async () => {
    setTotalByAppMonthLoading(true);
    setTotalByAppMonthError(false);
    try {
      const { from, to } = getByAppMonthRangeLocalDates(BY_APP_MONTH_WINDOW);
      const payload = { from, to };
      const st = Array.isArray(byAppFilterStatus) ? byAppFilterStatus : [];
      const pr = Array.isArray(byAppFilterPriority) ? byAppFilterPriority : [];
      const ao = Array.isArray(byAppFilterAppOrigin) ? byAppFilterAppOrigin : [];
      if (st.length > 0) payload.status = st;
      if (pr.length > 0) payload.priority = pr;
      if (ao.length > 0) payload.appOrigin = ao;
      const dt = Array.isArray(byAppFilterDemandType) ? byAppFilterDemandType : [];
      if (dt.length > 0) payload.demandType = dt;

      const res = await getTotalByAppByMonth(payload);
      const rawList = res?.content ?? res?.data ?? res;
      setTotalByAppMonthContent(Array.isArray(rawList) ? rawList : []);
    } catch (err) {
      console.error('Error fetching total by app by month:', err);
      setTotalByAppMonthContent([]);
      setTotalByAppMonthError(true);
    } finally {
      setTotalByAppMonthLoading(false);
    }
  }, [byAppFilterStatus, byAppFilterPriority, byAppFilterAppOrigin, byAppFilterDemandType]);

  useEffect(() => {
    fetchTotalByAppStatus();
  }, [fetchTotalByAppStatus, byAppFilterDemandType]);

  useEffect(() => {
    fetchTotalByAppByMonth();
  }, [fetchTotalByAppByMonth]);

  return (
    <PageContainer>
      <PageTitle>Dashboards • Por aplicação</PageTitle>
      <DashboardDemandTypeFilter
        selectedValues={byAppFilterDemandType}
        onValuesChange={setByAppFilterDemandType}
        disabled={totalByAppStatusLoading || totalByAppMonthLoading}
        fieldId="dashboard-by-app-demand-type-global"
      />
      <ChartsGrid>
        <ChartBlock>
          <TotalTicketsByAppStatusChart
            data={totalByAppStatus}
            loading={totalByAppStatusLoading}
            error={totalByAppStatusError}
            onRetry={fetchTotalByAppStatus}
            demandTypeFilter={byAppFilterDemandType}
          />
        </ChartBlock>
        <ChartBlock>
          <TotalTicketsByAppChart
            content={totalByAppMonthContent}
            seriesOrder={byAppChartSeriesOrder}
            loading={totalByAppMonthLoading}
            error={totalByAppMonthError}
            onRetry={fetchTotalByAppByMonth}
            filters={
              <CollapsibleFilters
                activeCount={
                  [
                    Array.isArray(byAppFilterAppOrigin) && byAppFilterAppOrigin.length > 0,
                    Array.isArray(byAppFilterStatus) && byAppFilterStatus.length > 0,
                    Array.isArray(byAppFilterPriority) && byAppFilterPriority.length > 0,
                  ].filter(Boolean).length
                }
              >
                <FiltersPanelBody>
                  <FieldFilter
                    label="Origem do app:"
                    fieldId="dashboard-by-app-origin-filter"
                    selectedValues={byAppFilterAppOrigin}
                    onValuesChange={setByAppFilterAppOrigin}
                    items={appOriginFilterItems}
                    disabled={totalByAppMonthLoading}
                  />
                  <FieldFilter
                    label="Status:"
                    fieldId="dashboard-by-app-status-filter"
                    selectedValues={byAppFilterStatus}
                    onValuesChange={setByAppFilterStatus}
                    items={statusFilterOptions}
                    disabled={totalByAppMonthLoading}
                  />
                  <FieldFilter
                    label="Prioridade:"
                    fieldId="dashboard-by-app-priority-filter"
                    selectedValues={byAppFilterPriority}
                    onValuesChange={setByAppFilterPriority}
                    items={PRIORITY_FILTER_OPTIONS}
                    disabled={totalByAppMonthLoading}
                  />
                </FiltersPanelBody>
              </CollapsibleFilters>
            }
          />
        </ChartBlock>
      </ChartsGrid>
    </PageContainer>
  );
};

export default TotalTicketsByAppPage;

